import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { COLORS } from '../../theme';
import { getAdminNavItems, isAdminNavActive } from './adminNavigation';

const prefereMenosMovimento = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * Faixa de páginas do topo no mobile (abaixo do logo/ícones).
 *
 * - A faixa rola na horizontal: por isso leva `data-no-swipe`, senão o swipe de
 *   troca de página brigaria com o arrasto natural das abas.
 * - O chip da página atual é centralizado a cada mudança de rota — inclusive
 *   quando a troca veio de um swipe em `useNavegacaoSwipeAdmin` —, para o
 *   usuário ver onde caiu mesmo que a aba estivesse fora da faixa visível.
 * - `aria-current="page"` marca o ativo para leitores de tela (o destaque
 *   laranja é só o retorno visual).
 */
const AdminMobileNav = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const ativoRef = useRef(null);
  const itens = getAdminNavItems();

  useEffect(() => {
    const chip = ativoRef.current;
    if (!chip || typeof chip.scrollIntoView !== 'function') return;

    chip.scrollIntoView({
      inline: 'center',
      block: 'nearest',
      behavior: prefereMenosMovimento() ? 'auto' : 'smooth',
    });
  }, [pathname]);

  return (
    <Box
      component="nav"
      aria-label="Páginas do painel"
      data-no-swipe
      sx={{
        display: 'flex',
        gap: 1,
        overflowX: 'auto',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        overscrollBehaviorX: 'contain',
        WebkitOverflowScrolling: 'touch',
        px: 2,
        pb: 1.2,
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {itens.map(({ icon: Icon, short, path }) => {
        const active = isAdminNavActive(pathname, path);

        return (
          <Box
            key={path}
            ref={active ? ativoRef : null}
            component="button"
            type="button"
            aria-current={active ? 'page' : undefined}
            onClick={() => navigate(path)}
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 0.6,
              border: `1px solid ${active ? COLORS.orange : 'rgba(242,238,231,0.2)'}`,
              bgcolor: active ? COLORS.orange : 'transparent',
              color: active ? COLORS.white : 'rgba(242,238,231,0.8)',
              borderRadius: '50px',
              px: 1.4,
              py: 0.6,
              cursor: 'pointer',
              fontSize: '0.68rem',
              fontWeight: 800,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <Icon sx={{ fontSize: 15 }} />
            {short}
          </Box>
        );
      })}
    </Box>
  );
};

export default AdminMobileNav;
